import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { 
  Card, 
  Paper, 
  Divider, 
  Box, 
  Typography, 
  Grid,
  CircularProgress,
  Alert
} from "@mui/material";
import Dialog from '@mui/material/Dialog';

const BetrInfosPager = ({ images, imgId, onClose }) => {
  // Simple image pager component (similar to UmleitungenPager)
  const [currentIndex, setCurrentIndex] = useState(imgId || 0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
      <Box sx={{ position: 'relative', mb: 2 }}>
        <img
          src={images[currentIndex]}
          alt={`Bild ${currentIndex + 1}`}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer'
              }}
            >
              ‹
            </button>
            <button
              onClick={handleNext}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer'
              }}
            >
              ›
            </button>
          </>
        )}
      </Box>
      {images.length > 1 && (
        <Typography variant="body2" color="text.secondary">
          {currentIndex + 1} von {images.length}
        </Typography>
      )}
      <button
        onClick={onClose}
        style={{
          marginTop: '16px',
          padding: '8px 16px',
          background: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Schließen
      </button>
    </Box>
  );
};

const BetrInfos = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalImgId, setModalImgId] = useState(0);

  useEffect(() => {
    const fetchFirmaInfos = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "FirmaInfos"));
        const docsData = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));

        // Sort by startDate (assumes format "dd.MM.yyyy")
        docsData.sort((a, b) => {
          try {
            const [dayA, monthA, yearA] = a.startDate.split(".");
            const [dayB, monthB, yearB] = b.startDate.split(".");
            const dateA = new Date(Number(yearA), Number(monthA) - 1, Number(dayA));
            const dateB = new Date(Number(yearB), Number(monthB) - 1, Number(dayB));
            return dateB - dateA; // Newest first
          } catch (error) {
            console.error('Error sorting dates:', error);
            return 0;
          }
        });

        setDocs(docsData);
        setError(null);
      } catch (error) {
        console.error('Error fetching FirmaInfos:', error);
        setError(`Fehler beim Laden der Firmen-Infos: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchFirmaInfos();
  }, []);

  // Filter docs to show only current/future ones
  const today = new Date();
  
  const filteredDocs = docs.filter(doc => {
    try {
      // Always show if endDate contains "bis auf weiteres"
      if (typeof doc.endDate === "string" && doc.endDate.toLowerCase().includes("bis auf weiteres")) {
        return true;
      }
      
      // Parse end date
      const [dayEnd, monthEnd, yearEnd] = doc.endDate.split(".");
      const endDate = new Date(Number(yearEnd), Number(monthEnd) - 1, Number(dayEnd));
      
      // Show if endDate is today or in the future
      return today <= endDate;
    } catch (error) {
      console.error('Error filtering doc:', doc, error);
      return true; // Show the doc if there's an error parsing the date
    }
  });

  // Handle image modal
  const handleImageClick = (images, index) => {
    setModalImages(images);
    setModalImgId(index);
    setOpenModal(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Lade Firmen-Infos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <section>
      <Typography variant="h6" gutterBottom sx={{ m: 2 }}>
        Betriebsinformationen ({filteredDocs.length})
      </Typography>

      {filteredDocs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', m: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Keine aktuellen Firmen-Infos verfügbar
          </Typography>
        </Paper>
      ) : (
        filteredDocs.map(doc => {
          // Check if the info is not yet valid
          let notYetValid = false;
          try {
            const [day, month, year] = doc.startDate.split(".");
            const startDate = new Date(Number(year), Number(month) - 1, Number(day));
            notYetValid = startDate > today;
          } catch (error) {
            console.error('Error parsing start date:', error);
          }

          return (
            <Card key={doc.id} sx={{ m: 2, p: 3, position: 'relative' }}>
              {notYetValid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Noch nicht gültig!
                </Alert>
              )}

              {/* Title - Made smaller */}
              <Paper elevation={0} sx={{ 
                textAlign: "center",
                fontWeight: "bold",
                p: 2,
                mb: 2,
                backgroundColor: '#f5f5f5'
              }}>
                <Typography variant="h5" component="div" sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }, // Responsive font size
                  wordWrap: 'break-word',
                  hyphens: 'auto'
                }}>
                  {doc.title}
                </Typography>
              </Paper>

              {/* Description */}
              {doc.description && (
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  {doc.description}
                </Typography>
              )}

              {/* Images */}
              {Array.isArray(doc.images) && doc.images.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                  {/* <Typography variant="h6" gutterBottom>
                    Bilder ({doc.images.length})
                  </Typography> */}
                  <Grid container spacing={2}>
                    {doc.images.map((img, idx) => (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <img
                          src={img}
                          alt={`${doc.title} Bild ${idx + 1}`}
                          style={{ 
                            width: "100%", 
                            height: "120px", 
                            objectFit: "cover",
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: "2px solid #ddd"
                          }}
                          onClick={() => handleImageClick(doc.images, idx)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* Metadata - Date Range first row, Created/Updated second row */}
              <Divider sx={{ my: 2 }} />

              {/* First Row - Date Range */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Typography variant="caption" sx={{ 
                  color: '#999', 
                  fontSize: '0.7rem',
                  fontWeight: 'normal'
                }}>
                  Gültigkeit: {doc.startDate} - {doc.endDate}
                </Typography>
              </Box>

              {/* Second Row - Creation/Update Metadata */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ 
                  color: '#999', 
                  fontSize: '0.7rem',
                  fontWeight: 'normal'
                }}>
                  {doc.createdAt && `Erstellt: ${doc.createdAt.toDate().toLocaleDateString('de-DE')}`}
                  {doc.updatedAt && ` | Aktualisiert: ${doc.updatedAt.toDate().toLocaleDateString('de-DE')}`}
                </Typography>
              </Box>
            </Card>
          );
        })
      )}

      <Divider style={{ margin: "20px 0" }} />
      <Paper elevation={2} sx={{ p: 2, textAlign: "center", m: 2 }}>
        <Typography variant="body1">
          Für weitere Informationen wenden Sie sich an die Betriebsleitung.
        </Typography>
      </Paper>

      {/* Image Modal */}
      <Dialog 
        open={openModal} 
        onClose={() => setOpenModal(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <BetrInfosPager 
          images={modalImages} 
          imgId={modalImgId} 
          onClose={() => setOpenModal(false)} 
        />
      </Dialog>
    </section>
  );
};

export default BetrInfos;